import { ref, toRef, computed, watch, watchEffect } from 'vue'
import { api } from '@/helpers/api'
import { useGoogleAPI } from '@/composables/utils/useGoogleAPI'
import { useValidation } from '@/composables/utils/useValidation'

import Welcome from '@/components/artist-direct-booking/steps/Welcome.vue'
import Register from '@/components/artist-direct-booking/steps/Register.vue'
import Login from '@/components/artist-direct-booking/steps/Login.vue'
import BookerInfo from '@/components/auth/booker-register/BookerInfo.vue'
import BookerBio from '@/components/auth/booker-register/BookerBio.vue'
import BookerAvatar from '@/components/auth/booker-register/BookerAvatar.vue'
import BookerFinal from '@/components/auth/booker-register/BookerFinal.vue'
import Cropper from '@/components/ui/images/Cropper.vue'

const URLS = {
    xhr: {
        mutate: {
            welcome: '/api/profile/is-user',
            register: '/register',
            login: '/api/login',
            update: '/api/profile/v1/settings',
            event_page: '/booking-manager'
        },
        fetch: {
            user: '/api/profile/v1/settings/user'
        }
    }
}
const step = ref(0)
const load = ref(false)
const imageCropped = ref(false)
const croppedImage = ref(null)
const socialAuth = ref(false)
const validation = ref(null)
const formData = ref({
    name: '',
    id: null,
    timezones: null,
    welcome: {
        email: ''
    },
    register: {
        first_name: '',
        last_name: '',
        email: '',
        password: ''
    },
    login: {
        password: ''
    },
    info: {
        address: '',
        lat: null,
        lon: null,
        phone_number: ''
    },
    bio: {
        bio: ''
    },
    avatar: []
})
const autocomplete = ref(null)

export const useBookerRegister = (props, { emit }) => {
    const showModal = toRef(props, 'showModal')
    const initRedirect = toRef(props, 'initRedirect')
    const authed = toRef(props, 'authed')

    const { isValid } = useValidation()
    const { steps, updateStep, currentStep } = useBookerRegisterSteps(formData, validation, socialAuth, croppedImage)

    const _needInfo = computed(
        () => !formData.value.info.address || !formData.value.info.lat || !formData.value.info.lon
    )
    const _needBio = computed(() => !formData.value.bio.bio)
    const _needAvatar = computed(() => !formData.value.avatar.length)

    const show = ref(showModal.value)

    watch(
        () => initRedirect.value,
        async (redirect) => {
            if (redirect) {
                const user = await _fetchUserInfo()
                _updateUserData(user)

                const hasEvents = user?.hasEvents ?? false

                if (_needInfo.value) return updateStep('info')
                if (_needBio.value) return updateStep('bio')
                if (_needAvatar.value) return updateStep('avatar')

                if (!hasEvents) {
                    showModal.value = true
                    return updateStep('final')
                }

                window.location.href = URLS.xhr.mutate.event_page
            }
        },
        { immediate: true }
    )
    watch(
        () => showModal.value,
        (showBaseModal) => {
            show.value = showBaseModal
        }
    )
    watch(
        () => show.value,
        (showBaseModal) => {
            emit('update:show-modal', showBaseModal)
            if (!showBaseModal && formData.value.id && !authed.value) {
                window.location.reload()
            }
        }
    )
    const { apiLoaded, initialize, lat, lng, fullAddress, placesTriggered } = useGoogleAPI()

    watchEffect(() => {
        const googleRef = autocomplete.value

        if (apiLoaded.value && ref) {
            initialize({
                mode: 'autocomplete',
                templateRef: googleRef,
                types: ['(cities)']
            })
        }
    })
    watchEffect(() => {
        if (placesTriggered.value) {
            const { address } = _formatGoogleAddress(fullAddress.value)
            formData.value.info.address = address
            formData.value.info.lat = lat
            formData.value.info.lon = lng
        }
    })

    function _formatGoogleAddress(address) {
        if (!address) return
        const addressArr = address.split(', ')

        const city = addressArr[0]
        const stateZip = addressArr[1]
        const state = stateZip.split(' ')[0]

        const fullAddress = city + ', ' + state

        return { address: fullAddress, city }
    }
    function toggleBookAuth(state) {
        show.value = state
    }
    function onClear(clear) {
        if (!clear) return
        _clearData(clear)
    }
    function _clearData(clear) {
        if (clear === 'name' || clear === 'id' || clear === 'timezones') {
            return
        }

        Object.keys(formData.value[clear]).forEach((key) => {
            if (clear === 'info' && (key === 'lat' || key === 'lon')) {
                formData.value[clear][key] = null
            } else if (clear === 'avatar') {
                updateStep('avatar')
                formData.value[clear] = []
            } else {
                formData.value[clear][key] = ''
            }
        })
    }
    function _prepUpdateData() {
        const form = new FormData()
        const data = {
            email: formData.value.welcome.email,
            first_name: formData.value.register.first_name,
            last_name: formData.value.register.last_name,
            phone_number: formData.value.info.phone_number,
            address: formData.value.info.address,
            lat: formData.value.info.lat,
            lon: formData.value.info.lon,
            description: formData.value.bio.bio ?? '',
            timezone: formData.value.timezones
        }

        for (let key in data) {
            form.append(key, data[key])
        }

        if (formData.value.avatar.length && formData.value.avatar[0] instanceof File) {
            form.append('avatar', formData.value.avatar[0])
        }
        form.append('_method', 'PUT')

        return form
    }

    async function onBack(identifier) {
        switch (identifier) {
            case 'welcome':
                return updateStep('welcome')
            case 'register':
                return updateStep('welcome')
            case 'login':
                return updateStep('welcome')
            case 'info':
                return updateStep('info')
            case 'bio':
                return updateStep('info')
            case 'avatar':
                return updateStep('bio')
            case 'cropper':
                return updateStep('avatar')
            default:
                return
        }
    }
    async function onNext(identifier) {
        switch (identifier) {
            case 'welcome':
                await _welcomeNext()
                break
            case 'register':
                await _registerNext()
                break
            case 'login':
                await _loginNext()
                break
            case 'info':
                await _infoNext()
                break
            case 'bio':
                await _bioNext()
                break
            case 'avatar':
                await _avatarNext()
                break
            case 'cropper':
                await _cropperNext()
                break
            default:
                return
        }
    }

    async function _welcomeNext() {
        if (!isValid(validation.value)) return updateStep('welcome')
        try {
            load.value = true

            const data = await api({
                url: URLS.xhr.mutate.welcome,
                method: 'POST',
                data: formData.value.welcome
            })
            if (data?.user) {
                formData.value.welcome.email = data.user.email
                formData.value.name = data.user.first_name

                return updateStep('login')
            } else {
                return updateStep('register')
            }
        } catch {
            return updateStep('register')
        } finally {
            load.value = false
        }
    }

    async function _registerNext() {
        if (!isValid(validation.value)) return updateStep('regiser')
        try {
            load.value = true

            const data = await api({
                url: URLS.xhr.mutate.register,
                method: 'POST',
                data: {
                    ...formData.value.register,
                    email: formData.value.welcome.email,
                    password_confirmation: formData.value.register.password
                }
            })
            formData.value.id = data.id

            const user = await _fetchUserInfo()

            _updateUserData(user)

            return updateStep('info')
        } catch {
            return updateStep('register')
        } finally {
            load.value = false
        }
    }

    async function _loginNext() {
        if (!isValid(validation.value)) return updateStep('login')

        try {
            load.value = true
            await api({
                url: URLS.xhr.mutate.login,
                method: 'POST',
                data: {
                    ...formData.value.login,
                    email: formData.value.welcome.email
                }
            })

            const user = await _fetchUserInfo()
            const hasEvents = user?.hasEvents ?? false

            _updateUserData(user)

            if (_needInfo.value) return updateStep('info')
            if (_needBio.value) return updateStep('bio')
            if (_needAvatar.value) return updateStep('avatar')

            if (!hasEvents) {
                showModal.value = true
                return updateStep('final')
            }

            window.location.href = URLS.xhr.mutate.event_page
        } catch {
            return updateStep('login')
        } finally {
            load.value = false
        }
    }

    async function _infoNext() {
        if (!isValid(validation.value)) return updateStep('info')

        try {
            load.value = true
            await api({
                method: 'post',
                url: `${URLS.xhr.mutate.update}/${formData.value.id}/update`,
                data: _prepUpdateData()
            })

            return updateStep('bio')
        } catch {
            return updateStep('info')
        } finally {
            load.value = false
        }
    }

    async function _bioNext() {
        if (!isValid(validation.value)) return updateStep('bio')

        try {
            load.value = true
            await api({
                method: 'post',
                url: `${URLS.xhr.mutate.update}/${formData.value.id}/update`,
                data: _prepUpdateData()
            })

            return updateStep('avatar')
        } catch {
            return updateStep('bio')
        } finally {
            load.value = false
        }
    }

    async function _avatarNext() {
        if (formData.value.avatar.length === 0) return updateStep('avatar')

        try {
            load.value = true
            await api({
                method: 'post',
                url: `${URLS.xhr.mutate.update}/${formData.value.id}/update`,
                data: _prepUpdateData()
            })

            return updateStep('final')
        } catch {
            return updateStep('avatar')
        } finally {
            load.value = false
        }
    }

    function _cropperNext() {
        if (croppedImage.value === null) return
        formData.value.avatar = [croppedImage.value]
        imageCropped.value = true
        updateStep('avatar')
    }

    async function _fetchUserInfo() {
        try {
            load.value = true
            const data = await api({
                method: 'get',
                url: URLS.xhr.fetch.user
            })

            return data.data[0]
        } finally {
            load.value = false
        }
    }

    function _updateUserData(user) {
        if (!user) return

        formData.value.id = user.id ?? null
        formData.value.welcome.email = user.email ?? ''
        formData.value.register.first_name = user.first_name ?? ''
        formData.value.register.last_name = user.last_name ?? ''
        formData.value.timezones = user.user_timezone.length ? user.user_timezone[0].id : user.timezone
        formData.value.info.phone_number = user.phone_number ?? ''
        formData.value.bio.bio = user.description ?? ''
        formData.value.avatar = user.avatar
            ? [
                  {
                      src: user.avatar
                  }
              ]
            : []
        imageCropped.value = true

        formData.value.info.address = user.address ?? ''
        formData.value.info.lat = user.lat ?? null
        formData.value.info.lon = user.lon ?? null
    }

    return {
        load,
        steps,
        show,
        autocomplete,
        croppedImage,

        currentStep,
        updateStep,

        onClear,
        onBack,
        onNext,
        toggleBookAuth
    }
}

export const useBookerRegisterSteps = (formData, validation, socialAuth) => {
    const avatar = computed(() => formData.value.avatar[0] ?? null)
    const steps = ref([
        {
            identifier: 'welcome',
            title: 'Sign Up',
            component: Welcome,
            actions: {
                back: null,
                next: 'register'
            },
            props: {
                redirectStorageData: {
                    'booker-register-modal': true,
                    'booker-register-step': 'info'
                },
                userData: formData,
                social: socialAuth,
                titleText: 'Welcome to Hedlyner',
                validation,
                actions: {
                    continue: 'welcome'
                }
            },
            clear: 'welcome'
        },
        {
            identifier: 'register',
            title: 'Sign Up',
            component: Register,
            actions: {
                next: 'info',
                back: 'welcome'
            },
            props: {
                userData: formData,
                titleText: 'Your name, please?',
                validation
            },
            clear: 'register'
        },
        {
            identifier: 'login',
            title: 'Sign Up',
            component: Login,
            actions: {
                next: 'info',
                back: 'welcome'
            },
            props: {
                redirectStorageData: {
                    'booker-register-modal': true,
                    'booker-register-step': 'final'
                },
                userData: formData,
                social: socialAuth,
                titleText: 'Welcome to Hedlyner',
                validation,
                actions: {
                    continue: 'login'
                }
            },
            clear: 'login'
        },
        {
            identifier: 'info',
            title: 'Complete Your Profile',
            component: BookerInfo,
            actions: {
                next: 'bio',
                back: null
            },
            props: {
                userData: formData,
                validation
            },
            clear: 'info'
        },
        {
            identifier: 'bio',
            title: 'Complete Your Profile',
            component: BookerBio,
            actions: {
                next: 'avatar',
                back: 'info'
            },
            props: {
                userData: formData,
                validation
            },
            clear: 'bio'
        },
        {
            identifier: 'avatar',
            title: 'Complete Your Profile',
            component: BookerAvatar,
            actions: {
                next: 'final',
                back: 'bio'
            },
            props: {
                userData: formData,
                imageCropped: imageCropped,
                avatar: avatar
            },
            clear: null
        },
        {
            identifier: 'final',
            title: 'Sign Up',
            component: BookerFinal,
            actions: {
                next: null,
                back: null
            },
            props: {},
            clear: null
        },
        {
            identifier: 'cropper',
            title: 'Edit Profile Picture',
            component: Cropper,
            actions: {
                next: 'avatar',
                back: 'avatar'
            },
            props: {
                image: avatar,
                croppedImage,
                isAvatar: true,
                showAvatar: false,
                circleCrop: true
            },
            clear: 'avatar'
        }
    ])

    function updateStep(identifier) {
        if (!identifier) {
            console.error('Incorrect step identifier in booking register.')
            return
        }
        step.value = steps.value.indexOf(steps.value.find((step) => step.identifier === identifier))
    }

    const currentStep = computed(() => steps.value[step.value])

    return {
        steps,
        updateStep,
        currentStep,

        formData
    }
}
